<template>
  <div class="container v-content-container">
    <div class="row">
      <div class="col-xs-12">
        <h1>
          <i class="fa fa-fw fa-lg fa-map-marker"></i
          >{{
            `${$label("venue.venue", "Venue")}${
              noDataExist ? "" : ": " + venueName
            }`
          }}
          <select
            v-model="selectedVenueId"
            class="form-control course-select pull-right"
            style="margin-right: 15px;"
          >
            <optgroup
              v-for="facility in facilities"
              :label="facility.name"
              v-bind:key="facility.id"
            >
              <option
                v-for="venue in facility.venues"
                :value="venue.id"
                v-bind:key="venue.id"
                >{{ venue.name }}</option
              >
            </optgroup>
          </select>
        </h1>
      </div>
    </div>

    <div class="row mt-8">
      <div class="col-xs-12">&nbsp;</div>
    </div>

    <error404 v-if="noDataExist" />

    <div class="row calendar-container" v-else>
      <date-picker
        v-model="selectedDate"
        :format="dateFormat"
        :readonly="true"
        :placeholder="dateFormat"
        class="input"
        id="selected-date"
      >
      </date-picker>
      <FullCalendar
        id="calendar"
        ref="calendar"
        class="calendar"
        :options="calendarOptions"
        :key="componentKey"
      >
        <template v-slot:eventContent="arg">
          <b>{{ arg.timeText }}</b>
          <i>{{ arg.event.title }}</i>
        </template>
      </FullCalendar>
      <div class="row calendar-legend-container">
        <div class="col-xs-3">
          <div class="calendar-legend-single calendar-legend text-center">
            Einzel
          </div>
          <div
            class="calendar-legend-reserved-single calendar-legend text-center"
          >
            Reservierung Einzel
          </div>
        </div>
        <div class="col-xs-3">
          <div class="calendar-legend-subscription calendar-legend text-center">
            Abo
          </div>
          <div
            class="calendar-legend-reserved-subscription calendar-legend text-center"
          >
            Reservierung Abo
          </div>
        </div>
        <div class="col-xs-3">
          <div class="calendar-legend-course calendar-legend text-center">
            Kurs
          </div>
          <div class="calendar-legend-holiday calendar-legend text-center">
            Feiertag
          </div>
        </div>
        <div class="col-xs-3">
          <div class="calendar-legend-timeframe calendar-legend text-center">
            Geschlossen / Gesperrt
          </div>
          <div class="text-center" style="padding-top: 2px;">
            <a id="ccTrue" class="custom-colors">Kundenfarben aktivieren</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Api from "@/api.js";

import Error404 from "@/components/Common/Error404";

import FullCalendar from "@fullcalendar/vue";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import luxonPlugin from "@fullcalendar/luxon";

import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import "vue2-datepicker/locale/de";

export default {
  components: {
    FullCalendar, // make the <FullCalendar> tag available
    DatePicker,
    Error404
  },

  data: function() {
    return {
      componentKey: 0,
      noDataExist: false,
      facilities: [],
      selectedVenue: null,
      selectedVenueId: null,
      bookings: [],
      selectedDate: new Date(),
      dateFormat: "DD.MM.YYYY",
      CalendarApi: null,
      calendarOptions: {
        plugins: [
          dayGridPlugin,
          timeGridPlugin,
          interactionPlugin, // needed for dateClick
          luxonPlugin
        ],
        headerToolbar: {
          left: "prev next today",
          center: "title",
          right: "dayGridMonth timeGridWeek timeGridDay"
        },
        views: {
          timeGrid: {
            titleFormat: "{{d.} LLLL} yyyy"
          }
        },
        height: "auto",
        allDaySlot: false,
        firstDay: 1,
        dayHeaderFormat: "EEE d.M",
        datesSet: this.handleChangeDate,
        slotMinTime: "07:00:00",
        slotMaxTime: "21:00:00",
        slotLabelFormat: "HH:mm",
        eventTimeFormat: "HH:mm",
        slotDuration: "00:30:00",
        initialView: "timeGridWeek",
        initialEvents: [], // alternatively, use the `events` setting to fetch from a feed
        editable: true,
        droppable: true,
        selectable: true,
        selectMirror: true,
        dayMaxEvents: true,
        weekends: true,
        select: this.handleDateSelect,
        eventClick: this.handleEventClick,
        eventsSet: this.handleEvents,
        eventDragStart: this.handleEventDragStart,
        eventDrop: this.handleEventDrop,
        eventReceive: this.handleEventDrop
        /* you can update a remote database when these fire:
        eventAdd:
        eventChange:
        eventRemove:
        */
      },
      currentEvents: [],
      starts_at: null,
      ends_at: null
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.selectedVenueId = vm.$route.params.id;
      vm.getFacilities(() => {
        vm.getVenue(() => {
          vm.getBookings();
        });
      });
    });
  },

  beforeRouteUpdate(to, from, next) {
    this.selectedVenueId = to.params.id;
    this.getFacilities(() => {
      this.getVenue(() => {
        this.getBookings();
      });
    });
    next();
  },

  mounted() {
    document
      .getElementsByClassName("fc-toolbar-chunk")[0]
      .prepend(document.getElementById("selected-date"));

    this.CalendarApi = this.$refs.calendar.$options.calendar;
  },

  methods: {
    getFacilities(successCallback) {
      const endpoint = this.$partnerEndpoint("facilities/simplified");

      Api()
        .get(endpoint)
        .then(response => {
          this.facilities = response.data.data;
          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(error => {
          console.log(error);
        });
    },

    getVenue(successCallback) {
      const endpoint = this.$partnerEndpoint("venues/" + this.selectedVenueId);

      Api()
        .get(endpoint)
        .then(response => {
          this.selectedVenue = response.data.data;
          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(error => {
          if (error.response.status === 404) {
            this.noDataExist = true;
          }
          console.log(error.response);
        });
    },

    getBookings(successCallback) {
      this.bookings = [];
      const endpoint = this.$partnerEndpoint("bookings");

      Api()
        .get(endpoint, {
          params: {
            from_start_date: this.starts_at.toISOString().replace(/T.*$/, ""),
            until_start_date: this.ends_at.toISOString().replace(/T.*$/, ""),
            // start_date: this.selectedDate.toISOString().replace(/T.*$/, ""),
            venue_id: this.selectedVenueId,
            page: 1,
            per_page: 1000
          }
        })
        .then(response => {
          this.bookings = response.data.data;
          this.metaData = response.data.meta;
          this.failedRequest = false;
          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(() => {
          this.failedRequest = true;
        });
    },

    handleDateSelect(selectInfo) {
      this.$router.push({
        name: "NewBookingVenue",
        query: {
          from_date_time: selectInfo.startStr,
          until_date_time: selectInfo.endStr,
          venue_ids: [this.selectedVenueId]
        }
      });
    },

    handleEventClick(clickInfo) {
      this.$router.push({
        name: "BookingVenueDetails",
        params: { booking_id: clickInfo.event._def.extendedProps.source.id }
      });
    },

    handleEvents(events) {
      this.currentEvents = events;
    },

    handleEventDragStart() {
      this.CalendarApi.setOption("snapDuration", "00:05:00");
    },

    handleEventDrop() {
      this.CalendarApi.setOption("snapDuration", "00:30:00");
    },

    handleChangeDate(dateInfo) {
      this.selectedDate = dateInfo.start;
      this.starts_at = dateInfo.start;
      this.ends_at = dateInfo.end;
      if (this.selectedVenueId) {
        this.getBookings();
      }
    },

    forceRerender() {
      this.componentKey += 1;
    }
  },

  computed: {
    venueName() {
      return this.selectedVenue ? this.selectedVenue.name : "";
    }
  },

  watch: {
    selectedVenueId(selectedVenueId) {
      this.$router.push({
        name: "VenueCalendar",
        params: { id: selectedVenueId }
      });
    },
    selectedDate(newDate) {
      newDate && this.CalendarApi.gotoDate(newDate);
    },
    bookings(newState) {
      let events = [];
      newState.forEach(booking => {
        const selectedVenueBooking = booking.venue_bookings.find(
          venueBooking =>
            venueBooking.venue.id === parseInt(this.selectedVenueId)
        );
        events.push({
          id: selectedVenueBooking.id,
          start: selectedVenueBooking.starts_at,
          end: selectedVenueBooking.ends_at,
          title: selectedVenueBooking.venue.name,
          source: booking
        });
      });
      this.currentEvents = events;
      this.CalendarApi.setOption("events", events);
    }
  }
};
</script>

<style>
.fc-toolbar-title {
  margin: 0;
  font-size: 16px;
}

ul {
  margin: 0;
  padding: 0 0 0 1.5em;
}

li {
  margin: 1.5em 0;
  padding: 0;
}

/* used for event dates/times */
b {
  margin-right: 3px;
}

.calendar-container {
  flex-grow: 1;
  padding: 0 2rem;
}

#selected-date {
  width: 110px !important;
}

#selected-date.mx-datepicker .mx-icon-calendar {
  display: none;
}

#selected-date.mx-datepicker .mx-input {
  padding: 6px 6px 6px 10px;
}

/* the calendar root */
.fc {
  flex-grow: 1;
}

.fc .fc-toolbar {
  align-items: flex-start;
}

.fc .fc-toolbar-title {
  font-size: 1.2em;
}

.fc .fc-toolbar-chunk {
  display: flex;
}

.fc .fc-button {
  border: none;
  text-transform: capitalize;
}

.fc-button:hover,
.fc-button:active,
.fc-button-active {
  opacity: 0.65;
}

.fc-button:hover,
.fc-button:active,
.fc-button:focus {
  border: none;
  outline: none;
}

.fc-button:focus {
  box-shadow: none !important;
}

.fc-button.fc-button-primary,
.fc-button.fc-button-primary:hover,
.fc-button.fc-button-primary:active {
  background-color: #0088cc !important;
}

.fc-prev-button,
.fc-next-button {
  padding: 0.4em 0.9em !important;
}
</style>
